/**
 * Floating bug report control — draggable, fixed under the site logo by default.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import BugReportModal from './BugReportModal';
import { useLanguage } from '../context/LanguageContext';
import './BugReportFloating.css';

const STORAGE_KEY_DESKTOP = 'bug-floating-position';
const STORAGE_KEY_MOBILE = 'bug-floating-position-mobile';
const DRAG_THRESHOLD = 5;

function getDefaultPosition() {
  const isNarrow = window.innerWidth <= 900;
  return {
    x: isNarrow ? 6 : 8,
    y: isNarrow ? 60 : 68,
  };
}

function clampPosition(pos, el) {
  const w = el?.offsetWidth || 56;
  const h = el?.offsetHeight || 72;
  const headerH = window.innerWidth <= 900 ? 60 : 64;
  return {
    x: Math.max(4, Math.min(pos.x, window.innerWidth - w - 4)),
    y: Math.max(headerH, Math.min(pos.y, window.innerHeight - h - 4)),
  };
}

/** Scan the DOM for visible modal/dialog elements and extract their headings. */
function capturePageContext() {
  const url = window.location.href;
  const pageTitle = document.title || '';

  const openPopups = [];
  const candidates = Array.from(
    document.querySelectorAll(
      '[role="dialog"], [aria-modal="true"], ' +
      '.modal-overlay, .modal-content, ' +
      '.contact-form-modal, .share-modal, .cart-panel, ' +
      '[class*="modal"], [class*="overlay"], [class*="popup"], [class*="drawer"]'
    )
  );

  const seen = new Set();
  candidates.forEach((el) => {
    // Skip our own bug report modal and invisible elements
    if (el.closest('.bug-report-overlay')) return;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;

    const label =
      el.getAttribute('aria-label') ||
      el.querySelector('h1,h2,h3,h4')?.textContent?.trim() ||
      el.getAttribute('class')?.split(' ')[0] ||
      'Unnamed popup';

    if (!seen.has(label)) {
      seen.add(label);
      openPopups.push(label);
    }
  });

  return { pageUrl: url, pageTitle, openPopups, capturedAt: new Date().toISOString() };
}

const BugReportFloatingButton = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [pageContext, setPageContext] = useState(null);
  const [position, setPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const [justDragged, setJustDragged] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const stackRef = useRef(null);
  const isDraggingRef = useRef(false);
  const positionRef = useRef(null);

  useEffect(() => { positionRef.current = position; }, [position]);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const storageKey = isMobile ? STORAGE_KEY_MOBILE : STORAGE_KEY_DESKTOP;
    const saved = localStorage.getItem(storageKey);
    const defaultPos = getDefaultPosition();
    if (saved) {
      try { setPosition(clampPosition(JSON.parse(saved), stackRef.current)); }
      catch { setPosition(defaultPos); }
    } else {
      setPosition(defaultPos);
    }
  }, []);

  useEffect(() => {
    if (position === null) return;
    const isMobile = window.innerWidth <= 768;
    const storageKey = isMobile ? STORAGE_KEY_MOBILE : STORAGE_KEY_DESKTOP;
    localStorage.setItem(storageKey, JSON.stringify(position));
  }, [position]);

  useEffect(() => {
    const handleResize = () =>
      setPosition((prev) => (prev ? clampPosition(prev, stackRef.current) : getDefaultPosition()));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getClientPoint = (e) =>
    e.touches?.length ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };

  const handlePointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const { x, y } = getClientPoint(e);
    const rect = stackRef.current?.getBoundingClientRect();
    if (!rect) return;
    setIsPointerDown(true);
    setDragStart({ x: x - rect.left, y: y - rect.top });
    isDraggingRef.current = false;
    e.preventDefault();
  };

  const handlePointerMove = useCallback((e) => {
    if (!isPointerDown || !positionRef.current) return;
    const { x, y } = getClientPoint(e);
    const newX = x - dragStart.x;
    const newY = y - dragStart.y;
    if (!isDraggingRef.current) {
      const dx = Math.abs(newX - positionRef.current.x);
      const dy = Math.abs(newY - positionRef.current.y);
      if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
        isDraggingRef.current = true;
        setIsDragging(true);
      } else return;
    }
    if (e.cancelable) e.preventDefault();
    setPosition(clampPosition({ x: newX, y: newY }, stackRef.current));
  }, [isPointerDown, dragStart]);

  const handlePointerUp = useCallback(() => {
    setIsPointerDown(false);
    if (isDraggingRef.current) {
      setIsDragging(false);
      isDraggingRef.current = false;
      setJustDragged(true);
      setTimeout(() => setJustDragged(false), 120);
    }
  }, []);

  useEffect(() => {
    if (!isPointerDown) return undefined;
    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', handlePointerUp);
    document.addEventListener('touchmove', handlePointerMove, { passive: false });
    document.addEventListener('touchend', handlePointerUp);
    if (isDragging) document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchmove', handlePointerMove);
      document.removeEventListener('touchend', handlePointerUp);
      document.body.style.userSelect = '';
    };
  }, [isPointerDown, isDragging, handlePointerMove, handlePointerUp]);

  const handleOpen = () => {
    if (isDragging || justDragged) return;
    setPageContext(capturePageContext());
    setIsOpen(true);
  };

  const currentPosition = position || getDefaultPosition();

  return (
    <>
      <div
        ref={stackRef}
        className={`bug-floating-stack ${isDragging ? 'dragging' : ''}`}
        style={{ left: `${currentPosition.x}px`, top: `${currentPosition.y}px` }}
      >
        <button
          type="button"
          className="bug-btn-floating"
          onClick={handleOpen}
          onMouseDown={handlePointerDown}
          onTouchStart={handlePointerDown}
          title={t.ui.bug.floatingTitle}
          aria-label={t.ui.bug.floatingAria}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          🐛
        </button>
        <span className="bug-floating-label" aria-hidden="true">
          {t.ui.bug.floatingLabel}
        </span>
      </div>
      <BugReportModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        pageContext={pageContext}
      />
    </>
  );
};

export default BugReportFloatingButton;
